import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { 
  FileText, 
  PlusCircle, 
  Search, 
  Car, 
  ArrowRight,
  Calendar,
  Filter,
  AlertCircle,
  CheckCircle2,
  Clock,
  Building2,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Folder
} from 'lucide-react';
import axios from 'axios';

import { API_URL } from '../utils/api';

const statusConfig = {
  draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700', icon: FileText },
  submitted: { label: 'Soumis', color: 'bg-blue-100 text-blue-700', icon: Clock },
  validated: { label: 'Validé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  tiers_contacted: { label: 'Tiers contacté', color: 'bg-amber-100 text-amber-700', icon: Clock },
  tiers_accepted: { label: 'Tiers accepté', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  tiers_refused: { label: 'Tiers refusé', color: 'bg-rose-100 text-rose-700', icon: AlertCircle },
  garage_selected: { label: 'Garage sélectionné', color: 'bg-blue-100 text-blue-700', icon: Car },
  quote_received: { label: 'Devis reçu', color: 'bg-amber-100 text-amber-700', icon: FileText },
  quote_validated: { label: 'Devis validé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  signature_pending: { label: 'Signature en attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  signed: { label: 'Signé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  payment_pending: { label: 'Paiement en attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  payment_confirmed: { label: 'Paiement confirmé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  repair_in_progress: { label: 'Réparation en cours', color: 'bg-blue-100 text-blue-700', icon: Car },
  completed: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  cancelled: { label: 'Annulé', color: 'bg-slate-100 text-slate-700', icon: AlertCircle },
  escalated_to_insurance: { label: 'Basculé assurance', color: 'bg-rose-100 text-rose-700', icon: AlertCircle }
};

// Statuts qui nécessitent une action du gestionnaire
const ACTION_NEEDED_STATUSES = [
  'submitted',       // Dossier à valider
  'quote_received',  // Devis à valider
  'signed',          // Paiement à confirmer
  'tiers_refused',   // À traiter / escalader
  'payment_confirmed' // À clôturer
];

const ClaimsList = () => {
  const { token, user } = useAuth();
  const [claims, setClaims] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedClients, setExpandedClients] = useState({});

  const isAdmin = user?.role === 'master_admin' || user?.role === 'super_admin' || user?.role === 'gestionnaire_c2';

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch claims
        let url = `${API_URL}/claims`;
        if (statusFilter !== 'all') {
          url += `?status=${statusFilter}`;
        }
        
        const claimsResponse = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setClaims(claimsResponse.data);

        // Fetch organizations for grouping (admin only)
        if (isAdmin) {
          const orgsResponse = await axios.get(`${API_URL}/organizations`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setOrganizations(orgsResponse.data);
          
          // Auto-expand all clients by default
          const expanded = {};
          orgsResponse.data.forEach(org => {
            expanded[org.id] = true;
          });
          setExpandedClients(expanded);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, statusFilter, isAdmin]);

  // Filter claims by search
  const filteredClaims = claims.filter(claim => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      claim.reference?.toLowerCase().includes(searchLower) ||
      claim.company_vehicle?.plate?.toLowerCase().includes(searchLower) ||
      claim.third_party?.last_name?.toLowerCase().includes(searchLower) ||
      claim.third_party?.first_name?.toLowerCase().includes(searchLower)
    );
  });

  // Group claims by organization
  const claimsByOrg = {};
  filteredClaims.forEach(claim => {
    const orgId = claim.organization_id || 'unknown';
    if (!claimsByOrg[orgId]) {
      claimsByOrg[orgId] = [];
    }
    claimsByOrg[orgId].push(claim);
  });

  // Get organization name
  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.name || 'Non assigné';
  };

  const toggleClient = (orgId) => {
    setExpandedClients(prev => ({
      ...prev,
      [orgId]: !prev[orgId]
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="claims-loading">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="flex gap-4">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-40" />
        </div>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    );
  }

  // Render claim card
  const renderClaimCard = (claim) => {
    const config = statusConfig[claim.status] || { label: claim.status, color: 'bg-slate-100 text-slate-700' };
    const needsAction = ACTION_NEEDED_STATUSES.includes(claim.status);
    
    return (
      <Link 
        key={claim.id} 
        to={`/claims/${claim.id}`}
        data-testid={`claim-item-${claim.id}`}
      >
        <Card className={`shadow-sm hover:shadow-md transition-all ml-8 ${
          needsAction 
            ? 'border-red-400 bg-red-50/50 hover:border-red-500' 
            : 'border-slate-100 hover:border-slate-200'
        }`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className={`hidden md:flex w-10 h-10 rounded-lg items-center justify-center flex-shrink-0 ${
                needsAction ? 'bg-red-100' : 'bg-slate-100'
              }`}>
                {needsAction 
                  ? <AlertCircle className="w-5 h-5 text-red-500" />
                  : <Car className="w-5 h-5 text-slate-600" />
                }
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <p className="font-semibold text-slate-900">{claim.reference}</p>
                  <Badge variant="secondary" className={`${config.color} text-xs`}>
                    {config.label}
                  </Badge>
                  {needsAction && (
                    <Badge className="bg-red-500 text-white text-xs">
                      Action requise
                    </Badge>
                  )}
                </div>
                
                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                  <span className="flex items-center gap-1">
                    <Car className="w-4 h-4 text-slate-400" />
                    {claim.company_vehicle?.plate}
                  </span>
                  {claim.driver_name && (
                    <span>Chauffeur: {claim.driver_name}</span>
                  )}
                  <span>
                    Tiers: {claim.third_party?.first_name} {claim.third_party?.last_name}
                  </span>
                  <span className="flex items-center gap-1 text-slate-400">
                    <Calendar className="w-4 h-4" />
                    {formatDate(claim.created_at)}
                  </span>
                </div>
              </div>
              
              <ArrowRight className={`w-5 h-5 ${needsAction ? 'text-red-400' : 'text-slate-400'}`} />
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  };

  return (
    <div className="space-y-6 pb-20 lg:pb-0" data-testid="claims-list">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Mes dossiers
          </h1>
          <p className="text-slate-500 mt-1">{claims.length} dossier{claims.length > 1 ? 's' : ''}</p>
        </div>
        <Link to="/claims/new">
          <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="new-claim-btn">
            <PlusCircle className="w-4 h-4 mr-2" />
            Nouveau dossier
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Rechercher par référence, plaque, tiers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-11"
            data-testid="search-input"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-48 h-11" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filtrer par statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="draft">Brouillon</SelectItem>
            <SelectItem value="submitted">Soumis</SelectItem>
            <SelectItem value="tiers_contacted">Tiers contacté</SelectItem>
            <SelectItem value="tiers_accepted">Tiers accepté</SelectItem>
            <SelectItem value="quote_received">Devis reçu</SelectItem>
            <SelectItem value="signature_pending">Signature en attente</SelectItem>
            <SelectItem value="signed">Signé</SelectItem>
            <SelectItem value="payment_confirmed">Paiement confirmé</SelectItem>
            <SelectItem value="completed">Terminé</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Claims grouped by client (Admin view) */}
      {isAdmin && organizations.length > 0 ? (
        <div className="space-y-4">
          {organizations.map((org) => {
            const orgClaims = claimsByOrg[org.id] || [];
            const isExpanded = expandedClients[org.id];
            const actionNeededCount = orgClaims.filter(c => ACTION_NEEDED_STATUSES.includes(c.status)).length;
            
            return (
              <div key={org.id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`client-folder-${org.id}`}>
                {/* Client Header (Folder) */}
                <button
                  onClick={() => toggleClient(org.id)}
                  className="w-full flex items-center gap-3 p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-slate-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-500" />
                  )}
                  
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <FolderOpen className="w-6 h-6 text-amber-500" />
                    ) : (
                      <Folder className="w-6 h-6 text-amber-500" />
                    )}
                    <div className="text-left">
                      <h3 className="font-semibold text-slate-900">{org.name}</h3>
                    </div>
                  </div>
                  
                  <div className="ml-auto flex items-center gap-2">
                    {actionNeededCount > 0 && (
                      <Badge className="bg-red-500 text-white text-xs" data-testid={`action-count-${org.id}`}>
                        {actionNeededCount} action{actionNeededCount > 1 ? 's' : ''}
                      </Badge>
                    )}
                    <Badge variant="secondary" className="bg-slate-200 text-slate-700">
                      {orgClaims.length} dossier{orgClaims.length > 1 ? 's' : ''}
                    </Badge>
                  </div>
                </button>
                
                {/* Claims inside folder */}
                {isExpanded && (
                  <div className="p-4 space-y-3 bg-white">
                    {orgClaims.length > 0 ? (
                      orgClaims.map(claim => renderClaimCard(claim))
                    ) : (
                      <p className="text-center text-slate-500 py-4 ml-8">
                        Aucun dossier pour ce client
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          
          {/* Unassigned claims */}
          {claimsByOrg['unknown']?.length > 0 && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="p-4 bg-slate-50">
                <div className="flex items-center gap-3">
                  <Folder className="w-6 h-6 text-slate-400" />
                  <h3 className="font-semibold text-slate-600">Non assignés</h3>
                  <Badge variant="secondary" className="ml-auto">
                    {claimsByOrg['unknown'].length}
                  </Badge>
                </div>
              </div>
              <div className="p-4 space-y-3 bg-white">
                {claimsByOrg['unknown'].map(claim => renderClaimCard(claim))}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Non-admin view: simple list */
        <div className="space-y-3">
          {filteredClaims.length > 0 ? (
            filteredClaims.map(claim => renderClaimCard(claim))
          ) : (
            <Card className="p-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600">Aucun dossier</h3>
              <p className="text-slate-500 mt-1">
                {search ? 'Aucun résultat pour cette recherche' : 'Créez votre premier dossier'}
              </p>
              {!search && (
                <Link to="/claims/new">
                  <Button className="mt-4 bg-amber-500 hover:bg-amber-600">
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Nouveau dossier
                  </Button>
                </Link>
              )}
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default ClaimsList;
